import React from 'react';
import { Outlet, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  CircularProgress,
  Tab,
  Tabs,
  Avatar
} from '@mui/material';
import { usePlatformAuth } from '../context/PlatformAuthContext';

export default function PlatformLayout() {
  const { user, ready, logout } = usePlatformAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (!ready) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/platform/login" state={{ from: location.pathname }} replace />;
  }

  const tab =
    location.pathname.startsWith('/platform/dashboard-config') &&
    (user.role === 'super_admin' || user.role === 'analyst')
      ? 'dashboard-config'
      : location.pathname.startsWith('/platform/system-settings') && user.role === 'super_admin'
      ? 'system-settings'
      : location.pathname.startsWith('/platform/support-tickets')
        ? 'support-tickets'
        : location.pathname.startsWith('/platform/login-sessions')
          ? 'login-sessions'
          : location.pathname.startsWith('/platform/audit-logs')
            ? 'audit-logs'
            : location.pathname.startsWith('/platform/permissions')
              ? 'permissions'
              : location.pathname.startsWith('/platform/tenant-roles')
                ? 'tenant-roles'
                : location.pathname.startsWith('/platform/tenant-users')
                  ? 'tenant-users'
                  : location.pathname.startsWith('/platform/api-keys')
                    ? 'api-keys'
                    : location.pathname.startsWith('/platform/webhooks') &&
                        (user.role === 'super_admin' || user.role === 'analyst')
                      ? 'webhooks'
                    : location.pathname.startsWith('/platform/export-jobs') &&
                        (user.role === 'super_admin' || user.role === 'analyst')
                      ? 'export-jobs'
                    : location.pathname.startsWith('/platform/usage-policies')
                      ? 'usage-policies'
                      : location.pathname.startsWith('/platform/feature-flags')
                        ? 'feature-flags'
                        : location.pathname.startsWith('/platform/tenant-subscriptions')
                          ? 'tenant-subscriptions'
                          : location.pathname.startsWith('/platform/billing')
                            ? 'billing'
                          : location.pathname.startsWith('/platform/knowledge-base')
                            ? 'knowledge-base'
                          : location.pathname.startsWith('/platform/notification-templates')
                            ? 'notification-templates'
                          : location.pathname.startsWith('/platform/tenants')
                            ? 'tenants'
                            : location.pathname.startsWith('/platform/plans')
                              ? 'plans'
                              : 'analytics';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography variant="h6">Platform</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button color="inherit" onClick={() => navigate('/platform/profile')} sx={{ textTransform: 'none' }}>
              <Avatar src={user.avatar_url || undefined} sx={{ width: 28, height: 28, mr: 1 }} />
              Profile
            </Button>
            <Typography variant="body2">{user.email}</Typography>
            <Button
              onClick={() => {
                logout();
                navigate('/platform/login');
              }}
            >
              Sign out
            </Button>
          </Box>
        </Toolbar>
      </AppBar>
      <Box sx={{ px: 3, pt: 0, pb: 3 }}>
        <Tabs
          value={tab}
          variant="scrollable"
          scrollButtons="auto"
          onChange={(_e, v) => {
            if (v === 'dashboard-config') navigate('/platform/dashboard-config');
            else if (v === 'system-settings') navigate('/platform/system-settings');
            else if (v === 'support-tickets') navigate('/platform/support-tickets');
            else if (v === 'login-sessions') navigate('/platform/login-sessions');
            else if (v === 'audit-logs') navigate('/platform/audit-logs');
            else if (v === 'permissions') navigate('/platform/permissions');
            else if (v === 'tenant-roles') navigate('/platform/tenant-roles');
            else if (v === 'tenant-users') navigate('/platform/tenant-users');
            else if (v === 'api-keys') navigate('/platform/api-keys');
            else if (v === 'webhooks') navigate('/platform/webhooks');
            else if (v === 'export-jobs') navigate('/platform/export-jobs');
            else if (v === 'usage-policies') navigate('/platform/usage-policies');
            else if (v === 'feature-flags') navigate('/platform/feature-flags');
            else if (v === 'tenant-subscriptions') navigate('/platform/tenant-subscriptions');
            else if (v === 'billing') navigate('/platform/billing/profiles');
            else if (v === 'knowledge-base') navigate('/platform/knowledge-base/articles');
            else if (v === 'notification-templates') navigate('/platform/notification-templates');
            else if (v === 'tenants') navigate('/platform/tenants');
            else if (v === 'plans') navigate('/platform/plans');
            else navigate('/platform/analytics');
          }}
          sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
        >
          <Tab label="Analytics" value="analytics" />
          {(user.role === 'super_admin' || user.role === 'analyst') && (
            <Tab label="Dashboard config" value="dashboard-config" />
          )}
          {user.role === 'super_admin' && <Tab label="System settings" value="system-settings" />}
          <Tab label="Sessions" value="login-sessions" />
          <Tab label="Support" value="support-tickets" />
          <Tab label="Audit logs" value="audit-logs" />
          <Tab label="Plans" value="plans" />
          <Tab label="Tenants" value="tenants" />
          <Tab label="Subscriptions" value="tenant-subscriptions" />
          <Tab label="Billing" value="billing" />
          <Tab label="Knowledge base" value="knowledge-base" />
          <Tab label="Notifications" value="notification-templates" />
          <Tab label="Tenant users" value="tenant-users" />
          <Tab label="API keys" value="api-keys" />
          {(user.role === 'super_admin' || user.role === 'analyst') && <Tab label="Webhooks" value="webhooks" />}
          {(user.role === 'super_admin' || user.role === 'analyst') && <Tab label="Export jobs" value="export-jobs" />}
          <Tab label="Usage policies" value="usage-policies" />
          <Tab label="Feature flags" value="feature-flags" />
          <Tab label="Tenant roles" value="tenant-roles" />
          <Tab label="Permissions" value="permissions" />
        </Tabs>
        <Outlet />
      </Box>
    </Box>
  );
}
